import type { Components, Theme } from '@mui/material/styles';
import { layout } from '@/constants/layout';

export function createComponentOverrides(): Components<
  Omit<Theme, 'components'>
> {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: ({ theme }: { theme: Theme }) => ({
          fontFeatureSettings: '"liga" 1, "calt" 1',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          colorScheme: theme.palette.mode,
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          mono: 'span',
          navSection: 'span',
          tableHeader: 'span',
          chip: 'span',
          helper: 'p',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: theme.typography.button.fontWeight,
          fontSize: theme.typography.button.fontSize,
          lineHeight: theme.typography.button.lineHeight,
        }),
        sizeSmall: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: theme.typography.subtitle2.fontWeight,
          fontSize: theme.typography.subtitle2.fontSize,
          minHeight: 44,
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.subtitle2.fontWeight,
        }),
        sizeSmall: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          lineHeight: theme.typography.body1.lineHeight,
        }),
        inputSizeSmall: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.helper,
          marginTop: theme.spacing(0.5),
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          ...theme.typography.tableHeader,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.default,
        }),
        body: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          lineHeight: theme.typography.body1.lineHeight,
        }),
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        li: ({ theme }) => ({
          ...theme.typography.caption,
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.navSection,
          color: theme.palette.text.secondary,
          backgroundColor: 'transparent',
          lineHeight: 1.5,
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({
          fontSize: theme.typography.subtitle2.fontSize,
          fontWeight: theme.typography.subtitle2.fontWeight,
          lineHeight: theme.typography.subtitle2.lineHeight,
        }),
        secondary: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        label: ({ theme }) => ({
          ...theme.typography.chip,
        }),
        sizeSmall: ({ theme }) => ({
          ...theme.typography.chip,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          lineHeight: theme.typography.body1.lineHeight,
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.h5,
          padding: theme.spacing(2.5, 3, 1),
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
        }),
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 1,
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: layout.headerHeight,
          height: layout.headerHeight,
          paddingTop: 0,
          paddingBottom: 0,
          '@media (min-width: 0px)': {
            minHeight: layout.headerHeight,
          },
          '@media (min-width: 600px)': {
            minHeight: layout.headerHeight,
          },
        },
      },
    },
  };
}
